import React from 'react';
import { CartItem } from '../../redux/slices/cartSlice';
import { OrderType } from '../../hooks/useCheckoutForm';
import { 
  getCartItemName, 
  getCartItemImage, 
  formatModifiersForDisplay 
} from '../../utils/cartUtils';

interface OrderSummaryProps {
  cartItems: CartItem[];
  orderType: OrderType;
  subtotal: number;
  deliveryFee: number;
  tax: number;
  total: number;
  savedDeliveryQuote?: { deliveryId: string; deliveryChargeCents: number } | null;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  cartItems,
  orderType,
  // subtotal,
  deliveryFee,
  total,
  savedDeliveryQuote,
}) => {
  const estimatedTime = orderType === 'delivery' ? '45-60' : '30-45';

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
      <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
      
      {/* Cart Items */}
      <div className="max-h-60 overflow-y-auto mb-6" role="region" aria-label="Order items">
        {cartItems.map((item) => (
          <div key={item.cartItemId} className="py-2 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {getCartItemImage(item) ? (
                  <img
                    src={getCartItemImage(item)}
                    alt={getCartItemName(item)}
                    className="w-10 h-10 object-cover rounded-md mr-2"
                  />
                ) : (
                  <div className="w-10 h-10 bg-orange-400 bg-opacity-20 flex items-center justify-center rounded-md mr-2">
                    <span className="text-sm font-bold text-orange-600">
                      {getCartItemName(item).charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <span className="font-medium">{item.quantity}x</span>
                  <span className="ml-2">{getCartItemName(item)}</span>
                </div>
              </div>
              <span className="font-medium">
                ${(item.totalPrice * item.quantity).toFixed(2)}
              </span>
            </div>
            
            {/* Display selected modifiers */}
            {formatModifiersForDisplay(item.selected.modifiers).length > 0 && (
              <div className="mt-1 ml-12">
                {formatModifiersForDisplay(item.selected.modifiers).map((modifier, index) => (
                  <div 
                    key={index}
                    className="flex justify-between text-xs text-gray-500"
                  >
                    <span>{modifier.options}</span>
                    <span>(${(modifier.price * item.quantity).toFixed(2)})</span>
                  </div>
                ))}
              </div>
            )}

          </div>
        ))}
      </div>
      
      {/* Order Totals */}
      <div className="space-y-4" role="region" aria-label="Order totals">
        {orderType === 'delivery' && savedDeliveryQuote && (
          <div className="flex justify-between">
            <span>Delivery Fee</span>
            <span>${savedDeliveryQuote.deliveryChargeCents.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-lg">
          <span>Total</span>
          <span>${(total + (savedDeliveryQuote?.deliveryChargeCents || 0)).toFixed(2)}</span>
        </div>
      </div>
      
      {/* Estimated Time */}
      <p className="text-sm text-gray-500 text-center mt-6">
        Estimated {orderType === 'delivery' ? 'delivery' : 'pickup'} time: {estimatedTime} minutes
      </p>
    </div>
  );
};
